import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { object, string, ref } from 'yup';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import { PATH_FORGOT_PASSWORD, PATH_LOGIN } from '@jwp/ott-common/src/paths';
import useForm from '@jwp/ott-hooks-react/src/useForm';
import useToggle from '@jwp/ott-hooks-react/src/useToggle';
import { modalURLFromLocation } from '@jwp/ott-ui-react/src/utils/location';
import Visibility from '@jwp/ott-theme/assets/icons/eye-on.svg?react';
import VisibilityOff from '@jwp/ott-theme/assets/icons/eye-off.svg?react';
import * as CustomAccountService from '@jwp/ott-common/src/services/integrations/CustomAccountService';
import { showToast } from '@jwp/ott-common/src/stores/ToastStore';

import AuthLayout from '../../containers/AuthLayout/AuthLayout';
import LoadingOverlay from '../../components/LoadingOverlay/LoadingOverlay';
import Button from '../../components/Button/Button';
import Icon from '../../components/Icon/Icon';
import { useAriaAnnouncer } from '../../containers/AnnouncementProvider/AnnoucementProvider';
import styles from '../SignInCognito/SignInCognito.module.scss';


const ResetPassword = () => {
  const { t } = useTranslation('account');
  const navigate = useNavigate();
  const location = useLocation();
  const announce = useAriaAnnouncer();
  const { siteName } = useConfigStore((s) => ({ siteName: s.config.siteName }));
  
  const [viewPassword, toggleViewPassword] = useToggle();
  const [viewConfirmPassword, toggleViewConfirmPassword] = useToggle();
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);

  useEffect(() => {
    const email = sessionStorage.getItem("fp_email");
    const code = sessionStorage.getItem("fp_code");
    if (!email || !code) {
      navigate(PATH_FORGOT_PASSWORD);
    }
  }, [navigate]);

  const { values, errors, submitting, handleSubmit, handleChange, validateField } = useForm<{ password: string; confirmPassword: string }>({
    initialValues: { password: '', confirmPassword: '' },
    silentValidation: true,
    validationSchema: object().shape({
      password: string()
        .required(t('registration.field_required', { field: t('reset.password') }))
        .min(8, t('registration.password_helper_text'))
        .matches(/[A-Za-z]/, t('reset.password_one_letter', { defaultValue: 'Password must contain at least one letter' }))
        .matches(/\d/, t('reset.password_one_number', { defaultValue: 'Password must contain at least one number' }))
        .matches(/[%\-_!@#$*=&.]/, t('reset.password_special_character', { defaultValue: 'Password must contain at least one special character' })),
      confirmPassword: string()
        .oneOf([ref("password")], t('registration.confirm_password_mismatch'))
        .required(t('registration.field_required', { field: t('reset.confirm_password') })),
    }),
    onSubmit: async ({ password }) => {
      const email = sessionStorage.getItem("fp_email");
      const code = sessionStorage.getItem("fp_code");
      if (!email || !code) { return; }

      setResetPasswordLoading(true);
      try {
        const response: any = await CustomAccountService.resetPassword(email, code, password);
        if (response?.errors && response.errors.length > 0) {
          throw new Error(response.errors[0].message);
        }
        if (response?.data) {
          sessionStorage.removeItem("fp_email");
          sessionStorage.removeItem("fp_code");
          navigate(modalURLFromLocation(location, 'reset-password-success-play'), { replace: true });
        }
      } catch (err: any) {
        console.error(err);
        throw err;
      } finally {
        setResetPasswordLoading(false);
      }
    },
    onSubmitError: ({ error }) => {
      let errorMessage = t('login.unexpected_error');
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      showToast(errorMessage, 'error');
    },
  });

  useEffect(() => {
    if (values.confirmPassword) {

      validateField('confirmPassword', values);
    }
  }, [values, validateField]);


  return (
    <AuthLayout title={t('reset.password_reset')} subtitle={t('reset.enter_new_password')}>
      <Helmet>
        <title>
          {t('reset.password_reset')} | {siteName}
        </title>
      </Helmet>

      <div className={styles.overlayWrap}>
        {submitting || resetPasswordLoading ? (
          <div className={styles.loaderLayer} aria-live="polite" aria-busy>
            <LoadingOverlay transparentBackground inline />
          </div>
        ) : null}

        <form onSubmit={handleSubmit} noValidate>
          <div className={styles.field}>
            <label htmlFor="reset-password" className={styles.fieldLabel}>
              {t('reset.password')}
              <span className={styles.required} aria-hidden>
                *
              </span>
            </label>
            <div className={`${styles.inputWrap} ${styles.inputWithToggle}`}>
              <input
                id="reset-password"
                name="password"
                type={viewPassword ? 'text' : 'password'}
                className={styles.input}
                value={values.password}
                onChange={handleChange}
                placeholder={t('reset.new_password_placeholder')}
                required
              />


              <button
                type="button"
                className={styles.toggleVisibility}
                onClick={() => toggleViewPassword()}
                aria-label={t('login.view_password')}
                aria-pressed={viewPassword}
              >
                <Icon icon={viewPassword ? Visibility : VisibilityOff} />
              </button>
            </div>
            {errors.password && (
              <p className={styles.helperError} role="alert">
                {errors.password}
              </p>
            )}
          </div>

          <div className={styles.field}>
            <label htmlFor="confirm-password" className={styles.fieldLabel}>
              {t('reset.confirm_password')}
              <span className={styles.required} aria-hidden>
                *
              </span>
            </label>
            <div className={`${styles.inputWrap} ${styles.inputWithToggle}`}>
              <input
                id="confirm-password"
                name="confirmPassword"
                type={viewConfirmPassword ? 'text' : 'password'}
                className={styles.input}
                value={values.confirmPassword}
                onChange={handleChange}
                placeholder={t('reset.confirm_password_placeholder')}
                required
              />
              <button
                type="button"
                className={styles.toggleVisibility}
                onClick={() => toggleViewConfirmPassword()}
                aria-label={t('login.view_password')}
                aria-pressed={viewConfirmPassword}
              >
                <Icon icon={viewConfirmPassword ? Visibility : VisibilityOff} />
              </button>
            </div>
            {errors.confirmPassword && (
              <p className={styles.helperError} role="alert">
                {errors.confirmPassword}
              </p>
            )}
          </div>

          <Button
            type="submit"
            className={styles.resetSubmit}
            color="primary"
            fullWidth
            size="large"
            disabled={submitting}
            label={t('reset.reset_password_button')}
          />
        </form>

        <p className={styles.footer}>
          {t('registration.already_account')}{' '}
          <Link className={styles.signUpLink} to={PATH_LOGIN}>
            {t('reset.sign_in')}
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
};

export default ResetPassword;
